import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';

import Alert from '@v2/shared/components/Alert';
import Price from '@v2/shared/components/Price';
import Button from '@v2/shared/components/Button';
import Spinner from '@v2/shared/components/Spinner';
import ButtonLink from '@v2/shared/components/ButtonLink';
import GiftOffer from '@v2/shared/components/GiftOffer';
import { WALLET_SHAPE_CLASSES } from '@v2/shared/components/Payment/types';
import { useCart } from '@v2/shared/lib/cart';
import { useSale } from '@v2/shared/lib/megaSale';
import { trackRemoveFromCart } from '@v2/shared/lib/analytics';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { Cart, LightboxDeal, PaymentConfig } from '@v2/shared/lib/types';

import FullScreenDialog from '@v2/shared/components/FullScreenDialog';
import CartLine from './CartLine';
import CartWalletButtons, { CART_WALLET_SHAPE } from './CartWalletButtons';
import LightboxDealDialog from './LightboxDealDialog';
import submitForm from './submitForm';

type Props = {
  /** Adds a second copy of the offered product as a gift. */
  onAddGift: (skuCode: string, offerCode: string | null) => Promise<void>;
  /** Braintree's settings, or null where the site holds no keys — see `CartWalletButtons`. */
  payment?: PaymentConfig | null;
};

/**
 * The cart, as a right-hand drawer.
 *
 * The legacy page kept the cart in a drawer too, and this keeps that — but it is v2's
 * own drawer rather than the legacy `snapper`. The two are not the same offer: snapper
 * lives in `layouts/full/drawer.html` and is driven by the `all_js_236` bundle, neither
 * of which a v2 page loads, so reusing it would have meant pulling legacy javascript
 * onto a page whose whole point is not to have any. FullScreenDialog slides in from the
 * same edge, traps focus, and is what the account menu already uses.
 *
 * The lines come from `GET /cart.json` rather than from the provider. What the provider
 * holds on a legacy-rendered page is the summary groups — a count and the search-alert
 * flags — because serializing the lines costs a product and sku query each. So the
 * drawer fetches when it opens, and every write after that answers with the whole cart
 * and needs no second request.
 */
export default function CartDrawer({ onAddGift, payment }: Props) {
  const { cartDrawer, giftOffer } = useSale();
  const { cart, cartCount, removeItem, updateItem, refresh } = useCart();
  const { buildUrl } = useUrlGenerator();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * That this drawer has been opened at least once — the switch from "not built yet" to
   * "built, and kept".
   *
   * Closing it used to unmount the whole panel, which took the wallet buttons with it, so
   * re-opening ran `braintree.client.create`, a data collector,
   * `applePay`/`googlePayment`/`paypalCheckout.create`, `isReadyToPay` and
   * `paypal.Buttons().render()` all over again — four network round trips and three
   * rebuilt sdks to show a customer a cart they had seen a moment earlier. Kept mounted,
   * a close is a `display: none` and a re-open is removing it.
   *
   * It is a latch rather than a constant `true` because the alternative is worse in the
   * other direction: mounted from the first render, the panel and its portal are built
   * for every visitor who never opens the cart — and this page is one bots crawl heavily
   * — and PayPal would be asked to draw its button into a hidden container, which its
   * sdk measures and cannot do. So: nothing until it is opened, everything from then on.
   */
  const [hasOpened, setHasOpened] = useState(false);

  const items = cart?.items;

  useEffect(() => {
    if (cartDrawer.isOpen) {
      setHasOpened(true);
    }
  }, [cartDrawer.isOpen]);

  // Asked for when the drawer opens, not on mount: a visitor who never opens it should
  // not pay for a cart read, and this page is one bots crawl heavily.
  useEffect(() => {
    if (!cartDrawer.isOpen) {
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    refresh()
      .catch((failure: unknown) => {
        if (!cancelled) {
          console.error('Could not load the cart', failure);
          setError('We could not load your cart. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cartDrawer.isOpen]);

  /*
   * The upgrade offer, once it has been priced and while the customer is answering it.
   *
   * It holds the cart it was priced against as well as the deal, because the offer names
   * cart lines by id and the covers and titles it draws come from them — pairing them
   * here is what stops the dialog describing a line that has since changed.
   *
   * `payload` is a wallet's authorization, and it is the reason the deal is checked at
   * this point rather than earlier. With Apple Pay, Google Pay or PayPal the customer has
   * already authorized an amount by the time we get here, and accepting the upgrade
   * changes it — which is exactly how the legacy cart has worked for a decade, and is not
   * a problem, because the amount is finally taken at checkout.
   */
  const [offer, setOffer] = useState<{ deal: LightboxDeal; cart: Cart; payload: string | null } | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const checkoutUrl = cart?.checkout_url || buildUrl('/cart');

  /** Straight to checkout, carrying a wallet's authorization if there is one. */
  const goToCheckout = (payload: string | null, url: string) => {
    if (payload) {
      submitForm(url, { xPaymentPayload: payload });
    } else {
      window.location.href = url;
    }
  };

  /**
   * The one door out of this drawer, whether the customer pressed Checkout or a wallet.
   *
   * It asks the server whether this cart is being offered an upgrade, and shows it if so
   * — with the wallets that happens after the payment sheet has closed and the payload is
   * in hand, which is where legacy puts it too: its lightbox intercepts the submit that
   * carries `xPaymentPayload`.
   *
   * The cart is re-read here rather than reused, because it is the read that prices the
   * deal and the two have to describe the same cart. Nothing else on the page pays for
   * that pricing: it is a per-line product load, coupon re-pricing and term lookup, and
   * this is the only moment it is worth anything.
   *
   * A failure goes to checkout. An upsell we could not price is revenue foregone; a
   * customer who cannot check out because of it is revenue lost, and they have in the
   * wallet case already authorized the payment.
   *
   * The drawer closes on the way, and that is a decision about the offer rather than about
   * the drawer. On a desktop the offer's backdrop covers the page but not the drawer,
   * which is fixed to the right edge above it — so the cart sat there un-dimmed beside a
   * dimmed page, competing with the one question being asked. Closing it is Carlos's call
   * (2026-09-02) and the right one: by this point the customer has committed to checking
   * out and their cart is no longer something they are deciding about.
   */
  const beginCheckout = async (payload: string | null) => {
    // An anchor cannot be disabled, so an impatient second click would otherwise ask the
    // server to price the same upgrade twice and show whichever answer landed last.
    if (checkingOut) {
      return;
    }

    setCheckingOut(true);

    try {
      const priced = await refresh({ includeLightboxDeal: true });

      if (priced.lightbox_deal?.items.length) {
        cartDrawer.close();
        setOffer({ deal: priced.lightbox_deal, cart: priced, payload });

        return;
      }
    } catch (failure) {
      console.error('Could not check for a lightbox deal', failure);
    } finally {
      setCheckingOut(false);
    }

    goToCheckout(payload, checkoutUrl);
  };

  /*
   * Refusing is a request, not a fall-through. `skip-lbdeal` records the refusal against
   * the deal and then redirects into checkout itself, so this is both the answer and the
   * way on — and it is the mechanism the legacy cart's decline link names, even though
   * legacy's own javascript cancels the link and re-submits its form instead, which is
   * why a decline is invisible in the numbers there and countable here.
   */
  const declineOffer = () => {
    if (!offer) {
      return;
    }

    goToCheckout(offer.payload, offer.deal.skip_url);
  };

  const guard = async (action: () => Promise<unknown>) => {
    setError(null);

    try {
      await action();
    } catch (failure) {
      console.error('The cart operation failed', failure);
      setError(failure instanceof Error ? failure.message : 'Something went wrong. Please try again.');
    }
  };

  const subtotal = (cart?.total_items_price ?? 0) - (cart?.total_discounts ?? 0);

  /*
   * Which line the gift offer hangs under: the one the add produced, and no other.
   *
   * It used to fall back to the first line when that one had gone, which is how removing a
   * product brought an offer back. Add A and the offer is under A; add B without answering
   * and the offer is under B; remove B and the fallback moved the surviving offer up onto
   * A, so a *removal* looked like it had re-raised an upsell the customer had walked past
   * twice. An offer whose line is gone has been answered.
   */
  const offerUnderItemId = giftOffer.cartItemId;
  const offerIsLive = !!items && items.some((item) => item.id === offerUnderItemId);

  /*
   * And once it is no longer live, it is dismissed rather than merely hidden.
   *
   * Hiding it would leave it in the provider, where the next thing that changed the cart
   * could bring it back. This is also what the legacy page did — `cart.product.removed`
   * was bound straight to `clearGiftLightBoxes` — and it covers a removal made anywhere,
   * not only through the button below.
   */
  useEffect(() => {
    if (giftOffer.product && items && !offerIsLive) {
      giftOffer.dismiss();
    }
  }, [giftOffer.product, items, offerIsLive]);

  return (
    <>
      <FullScreenDialog
        isOpen={cartDrawer.isOpen}
        toggle={cartDrawer.toggle}
        close={cartDrawer.close}
        title="Shopping Cart"
        slideInFrom="right"
        keepMounted={hasOpened}
      >
        <div className="flex flex-col min-h-full">
          <div className="flex-grow px-4">
            {error && (
              <Alert color="danger" className="mt-4">
                {error}
              </Alert>
            )}

            {loading && !items && (
              <p className="text-center text-slate-500 py-10">
                <Spinner className="text-2xl" />
                <span className="block mt-2 text-sm">Loading your cart...</span>
              </p>
            )}

            {items && items.length === 0 && (
              <div className="text-center py-10">
                <p className="text-slate-500 m-0">
                  <FontAwesomeIcon icon={faShoppingCart} className="text-3xl text-slate-300" />
                  <span className="block mt-3">Your cart is empty.</span>
                </p>
                {/* The footer with the other way back only draws when there is something to
                    check out, so an emptied cart would otherwise be a dead end. */}
                <Button color="secondary" size="sm" className="mt-4" icon={faArrowLeft} onClick={cartDrawer.close}>
                  Keep shopping this sale
                </Button>
              </div>
            )}

            {items && items.length > 0 && (
              <ul className="list-none m-0 p-0">
                {items.map((item) => (
                  <li key={item.id} className="list-none">
                    <CartLine
                      item={item}
                      /*
                        Reported after the write, and from the line as it was before it —
                        the response no longer carries it.

                        "After" means the request came back 2xx, which is not quite the
                        same as "the line was removed": `removeItem` notes that the
                        endpoint answers an unchanged cart for an id it cannot find rather
                        than a 404, so a line already gone in another tab is reported as a
                        removal here. Worth knowing; not worth a second read of the cart to
                        confirm what the customer just watched happen.
                      */
                      onRemove={(itemId) =>
                        guard(async () => {
                          await removeItem(itemId);
                          trackRemoveFromCart(item);
                        })
                      }
                      onToggleGift={(itemId, sendAsGift) => guard(() => updateItem(itemId, { sendAsGift }))}
                    />
                    {/*
                      The offer, directly under the line it is about.

                      It used to sit at the top of the drawer, which put "gift another copy
                      of X" above a list where X might be the fifth line down — the customer
                      had to find what it was talking about. Anchoring it to the line the add
                      produced makes the subject of the sentence the thing above it.
                    */}
                    {offerUnderItemId === item.id && giftOffer.product && giftOffer.skuCode && (
                      <GiftOffer
                        className="mb-4"
                        productName={giftOffer.product.name}
                        onAdd={() => guard(() => onAddGift(giftOffer.skuCode as string, giftOffer.offerCode))}
                        onDismiss={giftOffer.dismiss}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {cartCount > 0 && (
            <div className="sticky bottom-0 bg-white border-t-[1.5px] border-slate-200 p-4 mt-4">
              <div className="flex items-baseline justify-between mb-3">
                <span className="font-semibold text-slate-700">Subtotal</span>
                <Price value={subtotal} className="text-xl" />
              </div>
              {/*
                Still an anchor, and still `checkout_url` — the absolute url the cart itself
                built, including the offer codes and the site's own path prefix, which is
                why it is not assembled here.

                The click is intercepted so the upgrade offer gets its moment first, but the
                href stays real: it is what a middle click, a bot and a browser with no
                javascript follow, and all three should reach checkout rather than nothing.

                Its corner is the wallets', not `ButtonLink`'s own: the four of them stack
                into one control and are read as one. See `CART_WALLET_SHAPE`.
              */}
              <ButtonLink
                href={checkoutUrl}
                className={`w-full text-center ${WALLET_SHAPE_CLASSES[CART_WALLET_SHAPE]}`}
                onClick={(event) => {
                  event.preventDefault();
                  beginCheckout(null);
                }}
              >
                {checkingOut ? <Spinner /> : 'Checkout'}
              </ButtonLink>
              {/*
                Apple Pay, Google Pay and PayPal, directly under Checkout — the order the
                legacy sidebar puts them in. Each decides for itself whether the device and
                the wallet can offer it, so this row can end up with three buttons or none,
                and it collapses accordingly.
              */}
              <CartWalletButtons payment={payment} cart={cart} onAuthorized={beginCheckout} />
              {/*
                The way back to the sale, last, and the reason it is a button rather than
                the close cross in the corner.

                On a phone the drawer is the whole screen, so after an add the sale is gone
                and the only way back is a 24px cross in the top right — which reads as
                "dismiss this" and not as "there are more magazines". This page exists to
                get as many titles into one cart as possible, so continuing to shop is a
                first-class action and is drawn as one.

                It sits below the wallets rather than above them because the ways *out* of
                the cart belong together: Checkout and the three wallets are one block of
                "pay for this", and a "keep shopping" wedged into the middle of it separates
                a customer's payment method from the button they just decided not to press.
              */}
              <Button
                color="secondary"
                fullWidth
                size="sm"
                className="mt-2 whitespace-nowrap"
                icon={faArrowLeft}
                onClick={cartDrawer.close}
              >
                Keep shopping this sale
              </Button>
            </div>
          )}
        </div>
      </FullScreenDialog>

      {/*
        Beside the drawer rather than inside it, because by the time this draws the drawer
        has been closed — see `beginCheckout`. A dialog nested in a closed dialog is a
        dialog inside a `display: none` subtree whose only escape is its own portal, and
        HeadlessUI is entitled to treat the outer one's state as the inner one's context.
        This owes nothing to the drawer, so it does not live in it.
      */}
      {offer && (
        <LightboxDealDialog
          deal={offer.deal}
          cart={offer.cart}
          paymentPayload={offer.payload}
          onDecline={declineOffer}
        />
      )}
    </>
  );
}
